import { z } from "zod";
import { type FormationScheme, schemeLines } from "./lineup.js";

// SKORE plgrid cells: 5 rows × 9 columns; row -1 (col -1) = the goalkeeper's
// special slot. Rows: attack=0, midfield=2, defence=4 (odd rows stay empty for
// in-between placements — drag&drop can drop onto them too).
export const GRID_ROWS = 5;
export const GRID_COLS = 9;

// Display fields live inside the placement (the lineup-slot pattern) — the
// graphic works without a DB/API lookup; this is also what keeps it provider-agnostic.
export const gridPlacementSchema = z.object({
  playerId: z.string().min(1),
  firstName: z.string(),
  lastName: z.string(),
  number: z.number().int().nonnegative().nullable(),
  // The PFL API has no captain data — the operator marks it on the board.
  captain: z.boolean().default(false),
  row: z.number().int().min(-1).max(GRID_ROWS - 1),
  col: z.number().int().min(-1).max(GRID_COLS - 1),
});
export type GridPlacement = z.infer<typeof gridPlacementSchema>;

export const gridStateSchema = z.object({
  home: z.array(gridPlacementSchema).default([]),
  away: z.array(gridPlacementSchema).default([]),
});
export type GridState = z.infer<typeof gridStateSchema>;

export function emptyGrid(): GridState {
  return { home: [], away: [] };
}

/** Spreading n players across 9 columns, centred — a table taken from
 *  observing the SKORE editor. */
const COL_SPREAD: Record<number, number[]> = {
  1: [4],
  2: [2, 6],
  3: [2, 4, 6],
  4: [1, 3, 5, 7],
  5: [0, 2, 4, 6, 8],
};

/** Number of scheme lines → board rows (defence=4 … attack=0). 3-line schemes
 *  are 4/2/0 as in SKORE; with more lines the in-between rows are used too. */
const ROW_PLAN: Record<number, number[]> = {
  2: [4, 0],
  3: [4, 2, 0],
  4: [4, 3, 1, 0],
  5: [4, 3, 2, 1, 0],
};

/** Formation → 10 pitch cells (without the GK), in defence→attack order,
 *  left to right within each row. lineup.slots[1..10] follow this order. */
export function formationGridCells(scheme: FormationScheme): { row: number; col: number }[] {
  const lines = schemeLines(scheme);
  const rows = ROW_PLAN[lines.length] ?? [];
  return lines.flatMap((n, i) => (COL_SPREAD[n] ?? []).map((col) => ({ row: rows[i] ?? 0, col })));
}
